import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { CartProvider } from "@/context/CartContext";
import { AdminAuthProvider, useAdminAuth } from "@/context/AdminAuthContext";

import PublicLayout from "@/components/PublicLayout";
import AdminLayout from "@/components/AdminLayout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Founder from "@/pages/Founder";
import FeaturedBook from "@/pages/FeaturedBook";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import TrackOrder from "@/pages/TrackOrder";
import Multimedia from "@/pages/Multimedia";
import Contact from "@/pages/Contact";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";

// Admin console is code-split: its bundle only loads when an admin navigates
// there, keeping the initial download small for public visitors.
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminProductForm = lazy(() => import("@/pages/admin/AdminProductForm"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminInventory = lazy(() => import("@/pages/admin/AdminInventory"));
const AdminPricing = lazy(() => import("@/pages/admin/AdminPricing"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminDelivery = lazy(() => import("@/pages/admin/AdminDelivery"));
const AdminContacts = lazy(() => import("@/pages/admin/AdminContacts"));
const AdminMultimedia = lazy(() => import("@/pages/admin/AdminMultimedia"));

import "@/App.css";

function RequireAdmin({ children }) {
  const { isAuthenticated, loading } = useAdminAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F1EA] text-[#946f36]">
        Loading…
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AdminAuthProvider>
        <CartProvider>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#F4F1EA] text-[#946f36]">Loading…</div>}>
          <Routes>
            {/* Public site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/founder" element={<Founder />} />
              <Route path="/featured-book" element={<FeaturedBook />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/:category" element={<Shop />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
              <Route path="/track" element={<TrackOrder />} />
              <Route path="/multimedia" element={<Multimedia />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
            </Route>

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminLayout />
                </RequireAdmin>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/new" element={<AdminProductForm />} />
              <Route path="products/:id/edit" element={<AdminProductForm />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="delivery" element={<AdminDelivery />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="multimedia" element={<AdminMultimedia />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          {/* Bottom-right: the admin pages keep their primary action in the
              top-right corner, where a toast would sit directly on top of it. */}
          <Toaster position="bottom-right" />
        </CartProvider>
      </AdminAuthProvider>
    </BrowserRouter>
  );
}

export default App;
